import { useEffect, useState } from 'react';
import styles from './KioskLayout.module.css';
import { KioskHeader, KioskFooter } from '@/components/KioskComponents';
import { matchPath, Outlet, useNavigate } from 'react-router-dom';
import { LOCAL_STORAGE_KEYS } from '@/utils/constants';
import { Loader } from '@/components/Loader';
import { EKioskRouterNames } from '@/types';
import { useKioskStore } from '@/stores/kiosk';
import { navigateToActivationKiosk } from '@/utils/navigateToLogin';
import { useLanguageContext } from '@/features/LanguageContext';
import { useBrandingColors } from '@/hooks/useBrandingColors';
import { useCustomFavicon } from '@/hooks/useCustomFavicon';
import classNames from 'classnames';
import { KioskInactivity } from '@/components/KioskComponents/KioskInactivity';
import { PreloadQueriesKioskLayout } from '../PreloadQueriesKioskLayout';
import { useKioskAvailability } from '@/pages/Kiosk/hooks/useKioskAvailability';

const pathWithoutNavBarAndTopPanel = [
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__LOGIN_USER}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__SIGNUP_USER}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__FORGOT_PASSWORD}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__FIRST_LOGIN}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__PASSWORD_RESET_CONFIRMATION}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__RESET_PASSWORD}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__FORM_BUILDER_ENTRY_EDIT}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__FORM_BUILDER_ENTRY_VIEW}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__FIRST_LOGIN}/${EKioskRouterNames.KIOSK__FIRST_LOGIN__PARTICIPANT_FORM}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__CHAT}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__ENCOUNTER}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__WAITING_ROOM}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__SESSION_ENDED}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__LEFT}`,
];

export const KioskLayout = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { language } = useLanguageContext();

  const withoutHeaderAndFooter = pathWithoutNavBarAndTopPanel.some((path) =>
    matchPath(path, location.pathname),
  );

  const { kioskInstitution, getKioskInstitution } = useKioskStore((state) => ({
    getKioskInstitution: state.getKioskInstitution,
    kioskInstitution: state.kioskInstitution,
  }));

  useEffect(() => {
    const handleNoAccess = () => {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.KIOSK_ID);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.KIOSK_TOKEN);
      navigate(EKioskRouterNames.KIOSK_ACTIVATION);
    };
    const callback = async () => {
      const kioskId = localStorage.getItem(LOCAL_STORAGE_KEYS.KIOSK_ID);
      const kioskToken = localStorage.getItem(LOCAL_STORAGE_KEYS.KIOSK_TOKEN);
      if (!kioskId || !kioskToken) {
        handleNoAccess();
        return;
      }
      const kioskInstitution = await getKioskInstitution();
      if (!kioskInstitution) {
        handleNoAccess();
        return;
      }
      const currentSubdomain = window.location.host.split('.')[0];
      const kioskSubdomain =
        (kioskInstitution.whitelabel?.enabled &&
          kioskInstitution.whitelabel.subdomain) ||
        import.meta.env.VITE_DEFAULT_SUBDOMAIN;

      if (currentSubdomain !== kioskSubdomain) {
        navigateToActivationKiosk({
          subdomain: kioskSubdomain,
          accessKioskToken: true,
          kioskId,
          language,
        });
        localStorage.clear();
        return;
      }
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.CURRENT_INSTITUTION_ID,
        kioskInstitution.id,
      );
      setLoading(false);
    };

    void callback();
  }, [getKioskInstitution, language, navigate]);

  // Change background for body
  useEffect(() => {
    if (loading) return;
    document.body.style.backgroundColor = withoutHeaderAndFooter
      ? 'var(--colors-white)'
      : 'var(--colors-gray50)';
    return () => {
      document.body.style.backgroundColor = 'var(--colors-white)';
    };
  }, [loading, withoutHeaderAndFooter]);

  useBrandingColors(
    kioskInstitution?.whitelabel?.enabled
      ? kioskInstitution?.whitelabel?.primaryColor
      : undefined,
    kioskInstitution?.whitelabel?.enabled
      ? kioskInstitution?.whitelabel?.buttonTextColor
      : undefined,
  );

  useCustomFavicon(
    (kioskInstitution?.whitelabel?.enabled &&
      kioskInstitution?.whitelabel?.faviconImageUrl) ||
      undefined,
  );

  // this is needed on top layout level to logout already signed in user
  // on any page
  useKioskAvailability();

  if (loading) return <Loader />;

  return (
    <PreloadQueriesKioskLayout>
      <div
        className={classNames(styles.container, {
          [styles.withoutHeaderAndFooter]: withoutHeaderAndFooter,
        })}
      >
        {withoutHeaderAndFooter ? (
          <main className={styles.fullscreenContent}>
            <Outlet />
          </main>
        ) : (
          <>
            <KioskHeader />
            <main className={styles.content}>
              <Outlet />
            </main>
            <KioskFooter />
          </>
        )}

        <KioskInactivity />
      </div>
    </PreloadQueriesKioskLayout>
  );
};
